<template>
  <a-modal title="Filters" width="600px" :visible="visible" @cancel="cancel" :destroy-on-close="true" v-if="visible">
    <div class="grid" :style="{ overflow: 'auto', maxHeight: '650px' }">
      <div class="filter-section-title col-12">
        <span>Date range</span>
      </div>
      <b class="col-4">Start Date</b>
      <a-date-picker
        class="col-8"
        format="MM/DD/YYYY"
        v-model="filters.start_date"
        valueFormat="YYYY-MM-DD"
        placeholder="Start Date"
        @change="handleStartDateChange('start_date', 'end_date')"
      />

      <b class="col-4">End Date</b>
      <a-date-picker
        class="col-8"
        format="MM/DD/YYYY"
        v-model="filters.end_date"
        valueFormat="YYYY-MM-DD"
        placeholder="End Date"
        ref="endDatePicker"
        @change="handleEndDateChange('start_date', 'end_date')"
        :open="isEndDatePickerOpen"
        :default-picker-value="getEndDateDefaultPickerValue('start_date')"
        @openChange="handleEndDateOpenChange"
      />

      <template v-if="isStaffing">
        <div class="filter-section-title col-12">
          <span>Associate</span>
        </div>
        <a-radio-group v-model="filters.staffing" class="w-full" button-style="solid">
          <a-radio-button class="col-4 p-0 text-center" :value="undefined"> All </a-radio-button>
          <a-radio-button class="col-4 p-0 text-center" value="internal"> Internal </a-radio-button>
          <a-radio-button class="col-4 p-0 text-center" value="external"> External </a-radio-button>
        </a-radio-group>
      </template>

      <template v-if="filters.staffing === 'external'">
        <b class="col-4">Clients</b>
        <search-clients class="col-8" :multiply="true" :value.sync="filters.facility_project_ids"
      /></template>

      <div class="filter-section-title col-12">
        <span>Shift</span>
      </div>

      <b class="col-4">Shift Type</b>
      <a-select
        class="col-8"
        v-model="filters.shift_type"
        :allow-clear="true"
        :dropdown-match-select-width="false"
        placeholder="Shift Type"
      >
        <a-select-option value="worked"> Worked </a-select-option>
        <a-select-option value="pay_code"> Pay Code </a-select-option>
      </a-select>

      <template v-if="filters.shift_type === 'worked'">
        <b class="col-4">With attached pay codes</b>
        <a-checkbox class="col-8" v-model="filters.attached_pay_code" />
      </template>

      <b class="col-4">Shift Statuses</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.shift_statuses"
        placeholder="Status"
        :options="shiftStatuses"
        name="title"
        optionKey="key"
      />

      <b class="col-4">Ready For Pay Status</b>
      <custom-select
        class="col-8"
        :multiple="false"
        :value.sync="filters.ready_for_pay_status"
        placeholder="Ready For Pay Status"
        :options="notReadyStatuses"
        name="title"
        optionKey="key"
      />

      <template v-if="filters.ready_for_pay_status === 'not_ready'">
        <b class="col-4">Not Ready to Pay Reason</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value.sync="filters.not_ready_reasons"
          placeholder="Select Reason"
          :options="notReadyReasons"
          name="title"
          optionKey="key"
      /></template>

      <b class="col-4">Payment Statuses</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.shift_payment_statuses"
        placeholder="Status"
        :options="paymentStatuses"
        name="title"
        optionKey="key"
      />

      <b class="col-4">Special Rate</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.calculation_types"
        placeholder="Special Rate"
        :options="calculations"
        name="title"
        optionKey="key"
      />

      <template v-if="!filters.shift_type || filters.shift_type === 'pay_code'">
        <b class="col-4">Pay Codes</b>
        <custom-select
          class="col-8"
          :multiple="true"
          :value.sync="payCodeProxy"
          placeholder="Pay Codes"
          :options="payCodes"
          name="name"
          optionKey="id"
      /></template>

      <b class="col-4">Origins</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="filters.origins"
        placeholder="Origins"
        :options="origins"
        name="title"
        optionKey="key"
      />

      <div class="col-12 flex gap-2 flex-wrap mt-2">
        <a-button @click="toggleFilter('manually_created')" :type="filters.manually_created ? 'primary' : 'default'">
          Manually Created
        </a-button>
        <a-button @click="toggleFilter('adjusted')" :type="filters.adjusted ? 'primary' : 'default'">
          Adjusted
        </a-button>
      </div>

      <div class="filter-section-title col-12">
        <span>Violations</span>
      </div>

      <b class="col-4">Payroll Violations</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="payrollSelected"
        placeholder="Type"
        :options="payrollAuths"
        name="name"
        optionKey="key"
      />

      <b class="col-4">Disciplinary Violations</b>
      <custom-select
        class="col-8"
        :multiple="true"
        :value.sync="disciplinarySelected"
        placeholder="Type"
        :options="disciplinaryAuths"
        name="name"
        optionKey="key"
      />
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button :disabled="isEmptyFilters" @click="reset"> Reset </a-button>
        <a-button type="primary" @click="apply"> Apply </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Modal, Button, Select, Checkbox, DatePicker, Radio } from 'ant-design-vue'
import {
  SHIFT_STATUS,
  SHIFT_PAYMENT_REQUEST_STATUS,
  SHIFT_ORIGIN,
  SHIFT_NOT_READY_FOR_PAYMENT_FLAG,
  SHIFT_READY_FOR_PAYMENT_STATUS,
  CALCULATION_STATUSES
} from '@/store/modules/shifts/constants'
import { PAYMENT_TYPE, PAYMENT_WORK_STATUS } from '@/store/modules/payment/constants'
import { SCHEDULED_TYPE } from '@/store/modules/profile/constants'
import CustomSelect from '@/components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import SearchClients from '@/components/search-clients.vue'
import LinkedDateFilterMixin from '@/mixins/linkedDateFilterMixin'

export const PROFILE_SHIFTS_FILTER_ORDER = [
  'start_date',
  'end_date',
  'staffing',
  'shift_type',
  'attached_pay_code'
]

const filtersBaseState = Object.freeze({
  shift_statuses: ['deleted', 'missed', 'cancelled', 'completed', 'started', 'scheduled'],
  shift_payment_statuses: ['not_paid', 'paid', 'in_progress', 'partially_paid', 'draft', 'cancelled'],
  auth_types: undefined,
  pay_codes: undefined,
  end_date: undefined,
  start_date: undefined,
  shift_type: undefined,
  attached_pay_code: undefined,
  manually_created: undefined,
  with_pay_codes: undefined,
  adjusted: undefined,
  origins: undefined,
  staffing: null,
  facility_project_ids: undefined,
  pay_statuses: undefined,
  not_ready_reasons: undefined,
  ready_for_pay_status: undefined,
  calculation_types: undefined
})

export default {
  mixins: [LinkedDateFilterMixin],
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-select': Select,
    'a-checkbox': Checkbox,
    'a-select-option': Select.Option,
    'a-date-picker': DatePicker,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'search-clients': SearchClients,
    'custom-select': CustomSelect
  },
  data() {
    return {
      filters: { ...filtersBaseState },

      shiftStatuses: Object.entries(SHIFT_STATUS).map(([key, value]) => ({
        key,
        ...value
      })),

      paymentStatuses: Object.entries(SHIFT_PAYMENT_REQUEST_STATUS).map(([key, value]) => ({
        key,
        ...value
      })),

      paymentTypes: Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
        key,
        value
      })),

      notReadyReasons: Object.entries(SHIFT_NOT_READY_FOR_PAYMENT_FLAG).map(([key, value]) => ({
        key,
        ...value
      })),

      scheduleTypes: Object.entries(SCHEDULED_TYPE).map(([key, value]) => ({
        key,
        value
      })),

      payStatuses: Object.entries(PAYMENT_WORK_STATUS).map(([key, value]) => ({
        key,
        value
      })),

      origins: Object.entries(SHIFT_ORIGIN).map(([key, value]) => ({
        key,
        ...value
      })),

      notReadyStatuses: Object.entries(SHIFT_READY_FOR_PAYMENT_STATUS).map(([key, value]) => ({
        key,
        ...value
      })),

      calculations: [
        ...Object.entries(CALCULATION_STATUSES).map(([key, value]) => ({
          key,
          ...value
        }))
      ]
    }
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    currentFilters: {
      type: Object,
      required: true
    }
  },
  computed: {
    payCodes() {
      return this.$store.state.applicationState.projectPayCodes
    },

    mappedAuths() {
      return this.$store.state.applicationState.extendedShiftAuthTypes
    },

    disciplinaryAuths() {
      return this.mappedAuths.filter(item => item.auth_type === 'disciplinary')
    },

    payrollAuths() {
      return this.mappedAuths.filter(item => item.auth_type === 'payroll')
    },

    isEmptyFilters() {
      return JSON.stringify(filtersBaseState) === JSON.stringify(this.filters)
    },

    isStaffing() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    payCodeProxy: {
      get() {
        return this.filters.pay_codes
      },
      set(value) {
        this.filters.pay_codes = value
        this.filters.with_pay_codes = value && value.length ? true : undefined
      }
    },

    payrollSelected: {
      get() {
        return (this.filters.auth_types ?? []).filter(key => this.payrollAuths.some(auth => auth.key === key))
      },
      set(values) {
        this.filters.auth_types = [...values, ...this.disciplinarySelected]
      }
    },

    disciplinarySelected: {
      get() {
        return (this.filters.auth_types ?? []).filter(key => this.disciplinaryAuths.some(auth => auth.key === key))
      },
      set(values) {
        this.filters.auth_types = [...values, ...this.payrollSelected]
      }
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          const currentFilters = deepCopy(this.currentFilters)

          this.filters = {
            ...filtersBaseState,
            ...currentFilters,
            pay_codes: currentFilters.pay_codes ? currentFilters.pay_codes.map(Number) : undefined,
            facility_project_ids: currentFilters.facility_project_ids
              ? currentFilters.facility_project_ids.map(Number)
              : undefined,
            attached_pay_code:
              currentFilters.attached_pay_code === undefined ? undefined : JSON.parse(currentFilters.attached_pay_code),
            calculation_types: [...(currentFilters.calculation_types ?? [])]
          }
        }
      },
      immediate: true
    },
    'filters.shift_type': {
      handler(newVal) {
        if (newVal === 'worked') {
          this.filters.attached_pay_code = false
        } else {
          this.filters.attached_pay_code = undefined
        }

        if (newVal === 'worked' || !newVal) {
          this.filters.pay_codes = undefined
        }
      }
    },
    'filters.ready_for_pay_status': {
      handler(newVal) {
        if (newVal === 'not_ready') {
          this.filters.not_ready_reasons = undefined
        }
      }
    },
    'filters.staffing': {
      handler() {
        this.filters.facility_project_ids = undefined
      }
    }
  },
  methods: {
    reset() {
      if (this.isEmptyFilters) {
        this.cancel()
      } else {
        this.filters = deepCopy(filtersBaseState)
        this.$emit('update:currentFilters', this.filters)
      }
    },

    toggleFilter(type) {
      this.filters[type] = this.filters[type] ? undefined : true
    },

    cancel() {
      this.$emit('update:visible', false)
    },

    apply() {
      this.cancel()

      const { shift_statuses, shift_payment_statuses, ...rest } = this.filters

      const query = { ...rest }

      if (JSON.stringify(shift_statuses) !== JSON.stringify(filtersBaseState.shift_statuses)) {
        query.shift_statuses = shift_statuses
      }

      if (JSON.stringify(shift_payment_statuses) !== JSON.stringify(filtersBaseState.shift_payment_statuses)) {
        query.shift_payment_statuses = shift_payment_statuses
      }

      this.$emit('update:currentFilters', this.filters)
    }
  }
}
</script>

<style scoped>
.ant-calendar-picker {
  margin: 0;
}

.filter-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 0 2px;
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-transform: uppercase;
}

.filter-section-title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-color-base);
}
</style>
